/**
 * Auto-attach selling plan on /cart/add and /cart/change calls.
 *
 * Variants with a 1:1 selling plan attached in admin are tracked in the
 * window.AdegenVariantPlans map (populated by the
 * subscription-variant-plan-map snippet on each product context). When a
 * cart fetch is made without a selling_plan field, this wrapper looks up the
 * variant's plan in the map and injects it automatically.
 *
 * Loaded synchronously in <head> before any cart-related script so the
 * interceptor is in place when downstream calls fire.
 *
 * Scope is deliberately limited to JSON-string bodies (the recommendations
 * upsell, the 30->90 upgrade and the quiz checkout — contexts that should
 * default Rx products to a subscription). The PDP add-to-cart sends a FormData
 * body and is intentionally NOT enriched: the PDP has its own plan selector, so
 * injecting a plan there would override a shopper's explicit one-time choice.
 *
 * Kept as a global fetch interceptor on purpose (the cart refactor reviewed
 * replacing it with an explicit addToCart()/changeLine() helper — EVT-02). A
 * global wrap is a safety net: any JSON-body caller that forgets selling_plan
 * still gets the Rx subscription attached. With an opt-in helper, a future
 * caller that forgets to use it would silently add the Rx as a one-time
 * purchase — a real business bug. It does not race or overlap other cart
 * mutations, so it is not part of the event-driven decoupling.
 */
(function () {
    if (window.__adegenCartPlanInjectorInstalled) {
        return;
    }
    window.__adegenCartPlanInjectorInstalled = true;
    window.AdegenVariantPlans = window.AdegenVariantPlans || {};

    /**
     * Resolve the URL string from a fetch input argument (string or Request).
     *
     * @param {RequestInfo | URL} input
     * @returns {string}
     */
    function getUrl(input) {
        if (typeof input === 'string') {
            return input;
        }
        if (input instanceof URL) {
            return input.toString();
        }
        if (input && typeof input === 'object' && typeof input.url === 'string') {
            return input.url;
        }
        return '';
    }

    /**
     * Detect whether a given URL targets a Shopify cart endpoint that accepts a
     * selling_plan field (cart/add or cart/change).
     *
     * @param {string} url
     * @returns {boolean}
     */
    function isCartMutationUrl(url) {
        if (!url) {
            return false;
        }
        return /\/cart\/(add|change)(\.js)?(\?|$)/.test(url);
    }

    /**
     * Inject selling_plan into a cart-payload object when the variant is in
     * the window map and no selling_plan was provided. Mutates and returns the
     * same payload reference.
     *
     * @param {any} payload
     * @returns {any}
     */
    function injectSellingPlan(payload) {
        if (!payload || typeof payload !== 'object') {
            return payload;
        }
        const map = window.AdegenVariantPlans || {};

        const enrichItem = (item) => {
            if (!item || typeof item !== 'object') {
                return;
            }
            const variantId = Number(item.id);
            if (!variantId) {
                return;
            }
            const hasPlan = item.selling_plan !== undefined && item.selling_plan !== null && item.selling_plan !== '' && Number(item.selling_plan) > 0;
            if (hasPlan) {
                return;
            }
            const planId = map[variantId];
            if (planId) {
                item.selling_plan = planId;
            }
        };

        if (Array.isArray(payload.items)) {
            payload.items.forEach(enrichItem);
        } else if (payload.id !== undefined) {
            enrichItem(payload);
        }

        return payload;
    }

    /**
     * Build a fresh init object with the body rewritten to include the
     * injected selling_plan. Leaves headers and other init fields untouched.
     *
     * @param {RequestInit | undefined} init
     * @param {any} payload
     * @returns {RequestInit}
     */
    function withRewrittenBody(init, payload) {
        const next = init ? Object.assign({}, init) : {};
        next.body = JSON.stringify(payload);
        return next;
    }

    const originalFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
        try {
            const url = getUrl(input);
            if (!isCartMutationUrl(url) || !init || !init.body) {
                return originalFetch(input, init);
            }
            if (typeof init.body !== 'string') {
                // FormData / Blob bodies (the PDP form) are left untouched on
                // purpose — see the file header. Only JSON-string callers opt in.
                return originalFetch(input, init);
            }
            const payload = JSON.parse(init.body);
            injectSellingPlan(payload);
            return originalFetch(input, withRewrittenBody(init, payload));
        } catch (_) {
            return originalFetch(input, init);
        }
    };
})();
